import { useMemo } from "react";
import { useSiteSafe } from "@/lib/contexts/SiteContext";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { logFeatureEvent, type ObservabilityScope } from "./shared";

type PaywallSurface =
  | "plan_explorer"
  | "feature_gate"
  | "demo_modal"
  | "upgrade_modal"
  | "trial_expired_paywall"
  | "ai_sidebar_gate";

type PaywallTrigger =
  | "forced_paywall"
  | "manual"
  | "onboarding_post_otp"
  | "feature_denied"
  | "demo";

type LogPaywallShownParams = {
  surface: PaywallSurface;
  trigger: PaywallTrigger;
  featureKey?: string | null;
  persistent?: boolean;
  pathname?: string | null;
  scope?: ObservabilityScope;
};

/**
 * Paywall presentation is expected friction, not a defect. Emits a breadcrumb
 * plus a searchable Log so the team can filter who saw which lock surface.
 * Never creates a Sentry Issue.
 */
export const logPaywallShown = ({
  surface,
  trigger,
  featureKey,
  persistent,
  pathname,
  scope,
}: LogPaywallShownParams) =>
  logFeatureEvent({
    feature: "paywall",
    event: "paywall_shown",
    level: "warn",
    message: `Paywall shown (${surface})`,
    scope,
    attributes: {
      paywall_surface: surface,
      paywall_trigger: trigger,
      feature_key: featureKey ?? null,
      persistent: persistent ?? false,
      pathname: pathname ?? null,
    },
  });

/**
 * Resolves property / workspace / plan from whichever providers are mounted.
 * Hub and onboarding routes may lack SiteProvider; `property_id` is then null.
 */
export const usePaywallScope = (): ObservabilityScope => {
  const site = useSiteSafe();
  const workspace = useWorkspaceSafe();
  const { currentPlan } = useSubscription();

  const propertyId =
    workspace?.selectedPropertyId || site?.currentSite?.propertyId || site?.id || null;
  const domain = site?.currentSite?.domain ?? null;
  const workspaceId = workspace?.workspace?.workspaceId ?? null;
  const planTier = currentPlan ?? null;

  return useMemo(
    () => ({ propertyId, domain, workspaceId, planTier }),
    [propertyId, domain, workspaceId, planTier],
  );
};
